import { useTranslation } from 'react-i18next'
import { Hand, Wand2, Bot, Server, Boxes, Cloud, Users, Router, Database, Globe, type LucideIcon } from 'lucide-react'

/**
 * Canonical recovery-automation vocabulary for runbook task tagging.
 *
 * `value` MUST mirror backend/src/utils/runbook-automation.js, which is the source of
 * truth: the backend validates every write against its own list and coerces anything
 * unrecognised to 'manual'. If these drift, the symptom is a tag that quietly reverts
 * to Manual on save (and the backend logs the drift) rather than a broken screen.
 *
 * Method is a dropdown rather than free text on purpose: a mistyped handler would be
 * accepted, look armed to the engineer, and only fail at execution with unknown_method.
 *
 * Each option carries an icon and a one-or-two-word label so the table columns stay
 * narrow — the runbook table already has to fit task text, commands and times.
 * Labels are i18n KEYS, never literals: a missing key renders as the raw key string
 * (i18next returns the key), which is exactly how the untranslated labels slipped out.
 */

export interface AutomationOption {
    value: string
    labelKey: string
    icon: LucideIcon
    /** One line on what the mode actually means — the label alone does not carry it. */
    descKey?: string
}

/**
 * A recommended Method for a runbook, derived once per runbook display from what is
 * declared or captured about the object (never from its layer — see the backend
 * matcher). `null` from the API means nothing was confident, which is the common and
 * correct outcome; show nothing rather than advise a guess.
 */
export interface MethodSuggestion {
    method: string
    confidence: 'high' | 'medium' | 'low'
    reason: string
    alternates: { method: string; reason: string }[]
}

export const RUNBOOK_MODES: AutomationOption[] = [
    { value: 'manual', labelKey: 'objects.runbook.modeManual', icon: Hand, descKey: 'objects.runbook.modeManualDesc' },
    { value: 'assisted', labelKey: 'objects.runbook.modeAssisted', icon: Wand2, descKey: 'objects.runbook.modeAssistedDesc' },
    // The restore agent (vsrecovery) performs the task. Not 'automated', which was never stored:
    // the backend coerces it to manual (backend/src/utils/runbook-automation.js).
    { value: 'agent', labelKey: 'objects.runbook.modeAgent', icon: Bot, descKey: 'objects.runbook.modeAgentDesc' },
]

export const RUNBOOK_METHODS: AutomationOption[] = [
    { value: 'manual', labelKey: 'objects.runbook.methodManual', icon: Hand },
    { value: 'pxe-baremetal', labelKey: 'objects.runbook.methodPxeBaremetal', icon: Server },
    { value: 'hypervisor-vm', labelKey: 'objects.runbook.methodHypervisorVm', icon: Boxes },
    { value: 'cloud-import', labelKey: 'objects.runbook.methodCloudImport', icon: Cloud },
    { value: 'dc-restore', labelKey: 'objects.runbook.methodDcRestore', icon: Users },
    { value: 'network-device', labelKey: 'objects.runbook.methodNetworkDevice', icon: Router },
    { value: 'db-restore', labelKey: 'objects.runbook.methodDbRestore', icon: Database },
    { value: 'saas', labelKey: 'objects.runbook.methodSaas', icon: Globe },
]

/**
 * Methods a user can actually PICK. 'manual' is excluded on purpose: a task whose
 * Automation is Assisted or Automated is by definition not manual, and when Automation
 * is Manual the Method field is disabled anyway. It stays in RUNBOOK_METHODS above so
 * the stored default still resolves to a label for display.
 */
export const SELECTABLE_METHODS: AutomationOption[] = RUNBOOK_METHODS.filter(m => m.value !== 'manual')

/** Look up an option by stored value, falling back to the 'manual' entry. */
export function findMode(value: string | null | undefined): AutomationOption {
    const v = (value || 'manual').toLowerCase()
    return RUNBOOK_MODES.find(m => m.value === v) ?? RUNBOOK_MODES[0]
}

export function findMethod(value: string | null | undefined): AutomationOption {
    const v = (value || 'manual').toLowerCase()
    return RUNBOOK_METHODS.find(m => m.value === v) ?? RUNBOOK_METHODS[0]
}

/**
 * Coerce an untrusted value (AI output, an import) to a known tag. Mirrors the backend's
 * normaliseMode/normaliseMethod: unknown always becomes 'manual', never stored verbatim.
 */
export const normaliseMode = (value: string | null | undefined): string => findMode(value).value
export const normaliseMethod = (value: string | null | undefined): string => findMethod(value).value

/**
 * Tone per mode. The badges below are STATUS, not controls, so unlike the blue control
 * icons each mode gets its own tone — the whole point of the badge is that you can tell
 * manual from armed at a glance, down a column of twenty task cards.
 */
const MODE_TONE: Record<string, string> = {
    manual: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300',
    assisted: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300',
    agent: 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-900 dark:bg-purple-950/30 dark:text-purple-300',
}

/**
 * The manual/assisted/restore-agent pill shown on every task card, and (with `count`) in the
 * runbook's summary strip. Every task carries one — a card with no badge would be
 * indistinguishable from a card whose tag failed to load.
 */
export function AutomationBadge({
    mode,
    method,
    count,
    label,
    size = 'sm',
    className = '',
}: {
    mode?: string | null
    method?: string | null
    /** When set, renders "<count> Automated" instead of "Automated" (summary strip). */
    count?: number
    /** Overrides the mode label — e.g. the summary's "All tasks manual". */
    label?: string
    /** 'md' is the runbook-level summary: bigger, since it is read before anything else. */
    size?: 'sm' | 'md'
    className?: string
}) {
    const { t } = useTranslation()
    const m = findMode(mode)
    const Icon = m.icon
    // Method only qualifies an armed task; on a manual one it is always 'manual' and adds nothing.
    const showMethod = m.value !== 'manual' && !!method && method.toLowerCase() !== 'manual'
    const text = label ?? (count != null ? `${count} ${t(m.labelKey)}` : t(m.labelKey))
    const sizing = size === 'md' ? 'px-3 py-1 text-sm gap-1.5' : 'px-2 py-0.5 text-xs gap-1'
    return (
        <span
            className={`inline-flex items-center rounded-full border font-medium ${sizing} ${MODE_TONE[m.value] ?? MODE_TONE.manual} ${className}`}
        >
            <Icon className={`${size === 'md' ? 'h-4 w-4' : 'h-3 w-3'} flex-shrink-0`} aria-hidden />
            <span className="truncate">{text}</span>
            {showMethod && <span className="truncate opacity-70">· {t(findMethod(method).labelKey)}</span>}
        </span>
    )
}

/** Count tasks per mode. Anything unrecognised counts as manual, matching findMode. */
export function summariseAutomation(tasks: { mode?: string | null }[] | null | undefined) {
    const counts: Record<string, number> = { manual: 0, assisted: 0, agent: 0 }
    for (const task of tasks || []) counts[findMode(task?.mode).value] += 1
    return counts
}

/**
 * Runbook-level answer to "is there anything automated in here?" — shown at the top so an
 * engineer knows before scrolling whether this runbook is all hands-on or has armed steps.
 */
export function AutomationSummary({
    tasks,
    className = '',
}: {
    tasks: { mode?: string | null }[] | null | undefined
    className?: string
}) {
    const { t } = useTranslation()
    const counts = summariseAutomation(tasks)
    const total = counts.manual + counts.assisted + counts.agent
    if (total === 0) return null
    const armed = counts.assisted + counts.agent

    return (
        <div className={`flex flex-wrap items-center gap-2 ${className}`}>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('objects.runbook.automation')}:
            </span>
            {armed === 0 ? (
                // Still a badge, not muted text: "nothing here is automated" is a finding about
                // the runbook, and it has to read as loudly as a count of armed tasks would.
                <AutomationBadge
                    mode="manual"
                    size="md"
                    label={t('objects.runbook.allTasksManual', 'All tasks manual')}
                />
            ) : (
                RUNBOOK_MODES.filter((m) => counts[m.value] > 0).map((m) => (
                    <AutomationBadge key={m.value} mode={m.value} count={counts[m.value]} size="md" />
                ))
            )}
        </div>
    )
}

/** Icon + short label, for use inside a select trigger, item or table cell. */
export function AutomationOptionLabel({
    option,
    label,
    className = '',
}: {
    option: AutomationOption
    label: string
    className?: string
}) {
    const Icon = option.icon
    return (
        <span className={`inline-flex items-center gap-1.5 min-w-0 ${className}`}>
            {/* Blue matches the other action icons in the runbook editor (e.g. Add Image). */}
            <Icon className="h-3.5 w-3.5 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
            <span className="truncate">{label}</span>
        </span>
    )
}
